/**
 * AJAX add-to-cart for the single-product page.
 *
 * Intercepts the printable upload form and the regular WooCommerce add-to-cart
 * form, posts to the snapmagnetique_add_to_cart endpoint, and shows a themed
 * flash toast instead of a full page reload. The header cart count is bumped in
 * place, and for printable products the photo grid is reset for the next set
 * (the staging token is now committed to a cart line, so it must not be re-sent).
 *
 * Config arrives via wp_localize_script as window.SnapmagnetiqueCart. No-op when
 * that is absent (non-product pages), so it is safe to call everywhere.
 */

import { ensureNonces } from './nonces.js';

let flashRegion = null;

/** HTML-escape a string for safe insertion into the toast markup. */
function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (ch) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]
  );
}

/** Lazily create the fixed, polite live-region that stacks the toasts. */
function getFlashRegion() {
  if (flashRegion && document.body.contains(flashRegion)) {
    return flashRegion;
  }
  flashRegion = document.createElement('div');
  flashRegion.className = 'sm-flash-region';
  flashRegion.setAttribute('aria-live', 'polite');
  flashRegion.setAttribute('aria-atomic', 'false');
  document.body.appendChild(flashRegion);
  return flashRegion;
}

const ICON_CHECK =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
const ICON_ALERT =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>';

/**
 * Show a toast. `type` is 'success' or 'error'. Success toasts auto-dismiss
 * after a few seconds; errors linger a little longer. A close button and the
 * timer both tear the toast down.
 */
function flash({ title, detail, linkLabel, linkHref, type = 'success' }) {
  const region = getFlashRegion();
  const toast = document.createElement('div');
  toast.className = 'sm-flash sm-flash--' + type;
  toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

  let html = '<span class="sm-flash__icon">' + (type === 'error' ? ICON_ALERT : ICON_CHECK) + '</span>';
  html += '<div class="sm-flash__body">';
  html += '<span class="sm-flash__title">' + escapeHtml(title) + '</span>';
  if (detail) {
    html += '<span class="sm-flash__detail">' + escapeHtml(detail) + '</span>';
  }
  html += '</div>';
  if (linkHref && linkLabel) {
    html += '<a class="sm-flash__link" href="' + encodeURI(linkHref) + '">' + escapeHtml(linkLabel) + '</a>';
  }
  html += '<button type="button" class="sm-flash__close" aria-label="Dismiss">&times;</button>';
  toast.innerHTML = html;
  region.appendChild(toast);

  // Enter on the next frame so the transition runs.
  requestAnimationFrame(() => toast.classList.add('is-in'));

  let killed = false;
  const dismiss = () => {
    if (killed) {
      return;
    }
    killed = true;
    toast.classList.remove('is-in');
    toast.addEventListener('transitionend', () => toast.remove(), { once: true });
    setTimeout(() => toast.remove(), 600);
  };

  toast.querySelector('.sm-flash__close').addEventListener('click', dismiss);
  setTimeout(dismiss, type === 'error' ? 6000 : 4200);
}

/** Update every header cart-count badge and give it a brief bump animation. */
function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = String(count);
    el.classList.remove('is-bumped');
    // Force reflow so re-adding the class restarts the animation.
    void el.offsetWidth;
    el.classList.add('is-bumped');
  });
}

/** Resolve the product id from the form's hidden field or the submit button. */
function resolveProductId(form, submitter) {
  const hidden = form.querySelector('[name="product_id"]');
  if (hidden && hidden.value) {
    return hidden.value;
  }
  const button = form.querySelector('[name="add-to-cart"]');
  if (button && button.value) {
    return button.value;
  }
  return (submitter && submitter.value) || '';
}

/** The form's add-to-cart button (printable custom button or WC's native one). */
function findButton(form) {
  return form.querySelector('[data-add-to-cart]') ||
    form.querySelector('.single_add_to_cart_button') ||
    form.querySelector('[type="submit"]');
}

/** POST the form to the AJAX endpoint and react with a toast + count bump. */
function submitForm(form, submitter) {
  const cfg = window.SnapmagnetiqueCart;
  const i18n = (cfg && cfg.i18n) || {};
  const productId = resolveProductId(form, submitter);

  // Without a resolvable id we cannot build the request; fall back to a native
  // submit so the customer is never stuck.
  if (!cfg || !productId) {
    form.submit();
    return;
  }

  const body = new FormData(form);
  body.set('action', 'snapmagnetique_add_to_cart');
  body.set('product_id', productId);
  // The form keeps a hidden `add-to-cart` field for the no-JS fallback. It must
  // NOT ride into the AJAX request: WooCommerce's classic wp_loaded handler would
  // then add the product a second time (double-add / sold-individually error).
  // Our endpoint adds it once from product_id.
  body.delete('add-to-cart');

  const isPrintable = form.matches('[data-upload-form]');
  const button = findButton(form);
  const restoreText = button ? button.textContent : '';
  if (button) {
    button.disabled = true;
    button.dataset.loading = '';
    button.textContent = i18n.adding || 'Adding…';
  }

  let succeeded = false;

  // The nonce localized into this page expires long before the full-page cache
  // holding it does, so take a live one before posting.
  ensureNonces()
    .then(() => {
      body.set('nonce', cfg.nonce);
      return fetch(cfg.ajaxUrl, { method: 'POST', credentials: 'same-origin', body });
    })
    .then((response) => response.json())
    .then((res) => {
      if (res && res.success && res.data) {
        succeeded = true;
        flash({
          title: i18n.added || 'Added to cart',
          detail: res.data.name || '',
          linkLabel: i18n.viewCart || 'View cart',
          linkHref: res.data.cart_url || cfg.cartUrl,
          type: 'success',
        });
        if (typeof res.data.count !== 'undefined') {
          updateCartCount(res.data.count);
        }
        // The committed staging token is spent: clear the grid for a fresh set.
        // Drop the loading marker FIRST - the uploader's repaint leaves the
        // button alone while it is set, and the reset repaint must restyle it
        // (disabled, fresh "Add to cart · price" label).
        if (isPrintable) {
          if (button) {
            delete button.dataset.loading;
          }
          form.dispatchEvent(new CustomEvent('sm:reset-after-add'));
        }
      } else {
        const message = (res && res.data && res.data.message) || i18n.error || 'Sorry, this item could not be added to the cart.';
        flash({ title: i18n.error || 'Could not add to cart', detail: message, type: 'error' });
      }
    })
    .catch(() => {
      flash({ title: i18n.error || 'Could not add to cart', detail: '', type: 'error' });
    })
    .finally(() => {
      if (!button) {
        return;
      }
      delete button.dataset.loading;
      // A successful printable add re-renders its own button (disabled, priced)
      // via the reset handler, so leave it alone; otherwise restore it.
      if (isPrintable && succeeded) {
        return;
      }
      button.disabled = false;
      button.textContent = restoreText;
    });
}

/**
 * Wire the product-page add-to-cart forms to the AJAX endpoint. The printable
 * uploader keeps its button disabled until the set is complete, so a submit
 * that slips through while incomplete is ignored here (the uploader shows its
 * own message).
 */
export function initAjaxAddToCart() {
  if (!window.SnapmagnetiqueCart) {
    return;
  }

  const forms = [];
  const printable = document.querySelector('[data-product-config] [data-upload-form]');
  if (printable) {
    forms.push(printable);
  }
  document.querySelectorAll('.sm-woo-purchase form.cart').forEach((form) => {
    forms.push(form);
  });

  forms.forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const button = form.querySelector('[data-add-to-cart]');
      if (form.matches('[data-upload-form]') && button && button.disabled) {
        return;
      }
      submitForm(form, event.submitter || null);
    });
  });
}
